import type { SourcePos } from './ast.ts';
import { TypeMismatchError } from './errors.ts';
import { DashboardSpec } from './ops/dashboard/value.ts';

// Canonical re-export: `DashboardSpec` is the Value-union member, so
// consumers import it from here (the Value home) alongside the other
// Value kinds, even though the class body lives in the dashboard
// module to keep the tile-shape types co-located.
export { DashboardSpec } from './ops/dashboard/value.ts';

import type { LogicalPlan } from './plan.ts';
import type { Schema } from './schema/types.ts';

// Runtime value kinds the engine produces and consumes. Spec:
// SEXPR_ARCHITECTURE.md §5.1 (value kinds) and §11.1.
//
// Current slice covers scalars, vectors, Dataset, Schema, ColumnRef
// (Phase 4-prep 4-prep.6), DescSpec, and DashboardSpec (Phase 4c — the
// V-shape dashboard value returned by the top-level `(dashboard …)`
// op). The union grows further (QuerySpec, …) as the engine evolves —
// adding a member here is the trigger for adding it to ops that produce
// or consume it, a `VALUE_KIND_TABLE` row, and an `expect*` narrower.

export type Scalar = number | string | boolean | null | Date;

/**
 * Cell type: a single column-cell value. Widened from `Scalar` in
 * Phase 4-prep (substrate, design decision D2) so a column-cell can
 * carry a nested `Dataset` — the substrate's representation of a
 * timeseries reference and any future "lazy / deferred" tabular value.
 *
 * One level of nesting only: a Dataset cell's own cells must be
 * `Scalar`. The `MaterializedDataset` constructor enforces this
 * invariant and throws `NestedDatasetDepthError` on violation (see
 * PHASE_4_PREP.md, open question #4). A `Dataset` here is the abstract
 * supertype — a cell may hold either a `MaterializedDataset` or a
 * `LazyDataset` (the canonical timeseries-ref carrier).
 */
export type Cell = Scalar | Dataset;

export type Value = Scalar | readonly Value[] | Dataset | Schema | ColumnRef | DescSpec | DashboardSpec;

/**
 * Column-map shape: name → array of `Cell`s. Extracted as an alias
 * because both the `Dataset` field and its constructor parameter mention
 * the same nested type; centralising it keeps the runtime shape and the
 * constructor contract in sync.
 *
 * Cells widened from `Scalar` to `Cell = Scalar | Dataset` in Phase 4-prep
 * (substrate, design decision D2). Recursive nesting beyond one level is
 * rejected by `MaterializedDataset`'s constructor (`NestedDatasetDepthError`).
 */
export type Columns = Readonly<Record<string, readonly Cell[]>>;

/**
 * Tabular value. Phase 4-prep (sub-task 4-prep.2) made `Dataset` an
 * abstract supertype discriminated into `MaterializedDataset` (carries
 * `columns`) and `LazyDataset` (carries `plan`). Both always carry the
 * dataset's authoritative `Schema` (§5.4) so type-check, propagation,
 * and validation paths don't have to know which kind they're holding.
 *
 * The Schema's insertion order is the canonical column order (relevant
 * for materialised datasets; lazy datasets carry the same schema but
 * defer materialisation to fetch time).
 *
 * To narrow an abstract `Dataset` reference, prefer the
 * `isMaterialized(ds)` / `isLazy(ds)` predicates over `instanceof`
 * (both are equivalent runtime checks; the predicates make intent
 * explicit and narrow the TypeScript type via the `state` discriminator).
 *
 * Dataset is the canonical input/output of data-producing ops
 * (`query-knowledge-graph`, `transform`, …) and the right-hand side of
 * `(dataset name expr)` bindings.
 */
export abstract class Dataset {
  readonly kind = 'dataset' as const;
  abstract readonly state: 'materialized' | 'lazy';

  constructor(readonly schema: Schema) {}
}

/**
 * Materialised Dataset — all columns populated. The runtime shape that
 * existed before Phase 4-prep; pipeline verbs operate exclusively on
 * this kind (the dispatch wrapper in 4-prep.3 routes `LazyDataset`
 * inputs without ever calling a verb's `apply`).
 *
 * The constructor validates three invariants on the `(columns, schema)`
 * pair:
 *   1. All columns have equal length.
 *   2. The Schema's keys exactly match the columns' keys.
 *   3. One level of nesting: a Dataset cell's own cells must be
 *      `Scalar` (rejected with `NestedDatasetDepthError` on depth-2
 *      attempts).
 *
 * Downstream code can rely on all three without re-checking.
 */
export class MaterializedDataset extends Dataset {
  readonly state = 'materialized' as const;
  readonly columns: Columns;

  constructor(columns: Columns, schema: Schema) {
    super(schema);
    const lengths = Object.values(columns).map((c) => c.length);
    const distinct = new Set(lengths);
    if (distinct.size > 1) {
      const detail = Object.entries(columns)
        .map(([k, v]) => `${k}=${v.length}`)
        .join(', ');
      throw new TypeError(`Dataset columns must have equal length; got ${detail}`);
    }
    const columnNames = new Set(Object.keys(columns));
    const sameSize = columnNames.size === schema.size;
    const sameKeys = sameSize && [...columnNames].every((n) => schema.has(n));
    if (!sameKeys) {
      const cols = [...columnNames].sort().join(', ') || '(none)';
      const sch = [...schema.keys()].sort().join(', ') || '(none)';
      throw new TypeError(
        `Dataset columns and schema must reference the same column names; columns: ${cols}; schema: ${sch}`,
      );
    }
    // One-level nesting invariant (Phase 4-prep, D2 + open question #4).
    // Each cell may be a `Dataset`, but a Dataset cell's own cells must
    // be `Scalar`. We inspect the immediate cells: every materialised
    // Dataset we hold was constructed via this same constructor, so any
    // depth-2 violation surfaces here on the outer construction.
    // Lazy Dataset cells are opaque (no `columns` to inspect) — their
    // eventual materialisation is expected to honour the same invariant,
    // checked at materialisation time (4-prep.4).
    for (const [name, cells] of Object.entries(columns)) {
      const offender = cells.find((c) => c instanceof Dataset && datasetHasDatasetCell(c));
      if (offender !== undefined) {
        throw new NestedDatasetDepthError(name);
      }
    }
    this.columns = columns;
  }
}

/**
 * Lazy Dataset — a deferred dataset reference described by a
 * `LogicalPlan`. Carries the dataset's authoritative `Schema` (so
 * type-check / propagation paths work uniformly across materialised and
 * lazy datasets) but no `columns` — the data is fetched at
 * materialisation time (sub-task 4-prep.4).
 *
 * Invariant (not yet enforced by the constructor): the `plan`'s last
 * `PlanNode.schemaAfter` equals the dataset's `schema`. Producers
 * (currently none — `LazyDataset` is introduced ahead of qkg in 4a so
 * the dispatch wrapper in 4-prep.3 has a substrate to target) maintain
 * this manually; a check may be added in 4-prep.3 when the wrapper
 * starts building `LazyDataset`s from verb chains.
 */
export class LazyDataset extends Dataset {
  readonly state = 'lazy' as const;
  readonly plan: LogicalPlan;

  constructor(plan: LogicalPlan, schema: Schema) {
    super(schema);
    this.plan = plan;
  }
}

/**
 * Predicate: does any cell of `ds` hold a `Dataset`? Helper for the
 * `MaterializedDataset` constructor's one-level-nesting depth guard.
 * Lazy datasets have no inspectable cells, so depth-2 detection
 * short-circuits on lazy via `isMaterialized`.
 */
const datasetHasDatasetCell = (ds: Dataset): boolean =>
  isMaterialized(ds) && Object.values(ds.columns).some((cells) => cells.some((c) => c instanceof Dataset));

/**
 * Thrown by `MaterializedDataset`'s constructor when a column cell
 * contains a `Dataset` whose own cells include `Dataset`s — i.e., a
 * depth-2 nesting attempt. Phase 4-prep widens cells to `Scalar | Dataset`
 * for one level of nesting only; recursive nesting is unsupported until
 * a real use case appears (PHASE_4_PREP.md, open question #4).
 *
 * Extends the native `TypeError` to match the existing Dataset-constructor
 * error idiom (length-mismatch and schema/column-name-mismatch both throw
 * plain `TypeError`s); this error is a data-structure invariant, not an
 * evaluation-time failure, so it deliberately doesn't carry a
 * `SourcePos` and isn't part of the `ExprError` hierarchy.
 */
export class NestedDatasetDepthError extends TypeError {
  constructor(readonly column: string) {
    super(
      `Dataset cell in column \`${column}\` contains a Dataset whose own cells include Datasets; only one level of nesting is supported`,
    );
    this.name = 'NestedDatasetDepthError';
  }
}
/**
 * Symbolic reference to a column of a Dataset. Constructed by the
 * polymorphic `(col ...)` op (PHASE_4_PREP.md D9) and consumed at
 * materialisation / render time. The three call shapes of `(col ...)`
 * produce ColumnRef values that differ only in how `ds` is captured:
 *
 *   - **Named binding** — `(col foo-data revenue)`: `ds: string` (the
 *     dataset binding name; resolved via Env → Session at consumption
 *     time).
 *   - **Expression** — `(col (last foo-data) "fooTsCol")`: `ds: Dataset`
 *     (the dataset value already evaluated during construction).
 *   - **Struct path** — `(col foo-data location coords lat)`: same as
 *     either of the above, with a non-empty `path` walking into nested
 *     `struct` columns. Path walking is per-cell consumer code; the
 *     ref preserves the path verbatim.
 *
 * `Cell = Scalar | Dataset` — column-cells cannot hold `ColumnRef`s.
 * Refs live at the expression/Value layer, never inside column data.
 */
export class ColumnRef {
  readonly kind = 'column-ref' as const;
  constructor(
    readonly ds: string | Dataset,
    readonly column: string,
    readonly path: readonly string[] = [],
  ) {
    if (typeof ds === 'string' && ds.length === 0) {
      throw new TypeError('ColumnRef.ds (binding name) must be a non-empty string');
    }
    if (column.length === 0) {
      throw new TypeError('ColumnRef.column must be a non-empty string');
    }
    if (path.some((p) => p.length === 0)) {
      throw new TypeError('ColumnRef.path must not contain empty strings');
    }
  }
}
/**
 * Sort-direction wrapper around a `ColumnRef`. Constructed by the
 * `(desc <col-ref>)` op and consumed by the `sort` verb to drive
 * descending ordering on a per-key basis.
 *
 * The wrapper is intentionally minimal — only the column reference is
 * carried; the direction is implicit in the class identity. Ascending
 * keys flow through `sort` as bare `ColumnRef` values; descending keys
 * flow as `DescSpec`. `sort` discriminates with `isDescSpec` and
 * propagates the wrapped `ColumnRef` to the comparator.
 *
 * `Cell = Scalar | Dataset` — column-cells cannot hold `DescSpec`
 * either; like `ColumnRef`, it lives strictly at the Value layer.
 */
export class DescSpec {
  readonly kind = 'desc-spec' as const;
  constructor(readonly column: ColumnRef) {}
}

// Lookup table for `describeValueKind`. Each row pairs a runtime predicate
// with its diagnostic label. Adding a new `Value` kind is a one-row change.
// Order matters: `null` must come before any object/array check
// (`typeof null === 'object'`). `Schema` is matched via `instanceof Map`
// because `Schema = ReadonlyMap<string, ColumnType>`; no other `Value`
// kind is Map-shaped, so the discrimination is unambiguous. Both
// `MaterializedDataset` and `LazyDataset` extend the abstract `Dataset`
// class, so the single `instanceof Dataset` row matches either kind.
const VALUE_KIND_TABLE: readonly { readonly match: (v: Value) => boolean; readonly label: string }[] = [
  { match: (v) => v === null, label: 'null' },
  { match: (v) => typeof v === 'boolean', label: 'boolean' },
  { match: (v) => typeof v === 'number', label: 'number' },
  { match: (v) => typeof v === 'string', label: 'string' },
  { match: (v) => v instanceof Date, label: 'date' },
  { match: (v) => v instanceof Dataset, label: 'dataset' },
  { match: (v) => v instanceof ColumnRef, label: 'column-ref' },
  { match: (v) => v instanceof DescSpec, label: 'desc-spec' },
  { match: (v) => v instanceof DashboardSpec, label: 'dashboard-spec' },
  { match: (v) => v instanceof Map, label: 'schema' },
  { match: Array.isArray, label: 'vector' },
];

/**
 * Human-readable name for a Value's runtime kind. Used in diagnostics
 * where we want to tell the caller "I expected a Dataset, got a number".
 * Dispatches via `VALUE_KIND_TABLE` — the first matching predicate's label
 * wins; if none match (a shape outside `Value`), falls back to `'unknown'`.
 */
export const describeValueKind = (v: Value): string =>
  VALUE_KIND_TABLE.find(({ match }) => match(v))?.label ?? 'unknown';

/**
 * Build a `Value → T` narrower from a runtime predicate + an expected-
 * kind label. Centralises the three-line "predicate fails → throw
 * `TypeMismatchError(describeValueKind(v), <expected>, context, pos)`;
 * predicate passes → return as-is" shape that `expectDataset`,
 * `expectSchema`, and `expectColumnRef` all share. Adding a new
 * Value-kind narrower is now a one-liner: write the discriminator
 * predicate and call this factory with the expected-kind label.
 *
 * Sibling of `expectDatasetState` (which adds a second layer for
 * state-narrowing between MaterializedDataset and LazyDataset); the
 * two helpers are intentionally separate because their error messages
 * differ — state-narrowing names the actual dataset state (e.g.
 * `'a lazy Dataset'`) rather than the generic value-kind label.
 *
 * `expected` is the human-readable noun-phrase shown in the error
 * (e.g. `'a Dataset'`); `context` is the op-call fragment; `pos` is
 * the source-position sidecar lookup.
 */
const expectValueKind =
  <T extends Value>(predicate: (v: Value) => v is T, expected: string) =>
  (value: Value, context: string, pos: SourcePos): T => {
    if (!predicate(value)) {
      throw new TypeMismatchError(describeValueKind(value), expected, context, pos);
    }
    return value;
  };

/**
 * Narrow a `Value` to a `Dataset`, throwing `TypeMismatchError` if the
 * runtime kind is anything else. Centralises the
 * "I expected a Dataset; got a ..." failure mode so the expected-kind
 * string `'a Dataset'` lives in one place. Accepts both
 * `MaterializedDataset` and `LazyDataset` — the abstract supertype is
 * what most consumers want. `context` is the op-call fragment shown in
 * the error (e.g. `(dataset wells ...)`).
 */
export const expectDataset = expectValueKind((v): v is Dataset => v instanceof Dataset, 'a Dataset');
/**
 * Narrow a `Value` to a `Schema`, throwing `TypeMismatchError` if the
 * runtime kind is anything else. Centralises the "I expected a Schema;
 * got a ..." failure mode. `context` is the op-call fragment shown in
 * the error (e.g. `(schema-of wells)`). `Schema` is a `ReadonlyMap`
 * at runtime; no other `Value` kind is Map-shaped, so `instanceof Map`
 * is sufficient discrimination.
 */
export const expectSchema = expectValueKind((v): v is Schema => v instanceof Map, 'a Schema');

/**
 * Discriminator: is `ds` a `MaterializedDataset` (vs a `LazyDataset`)?
 * Prefer over `instanceof MaterializedDataset` at call sites that
 * already have an abstract `Dataset` reference — the predicate makes
 * intent explicit and narrows the TypeScript type via the `state`
 * discriminator.
 */
export const isMaterialized = (ds: Dataset): ds is MaterializedDataset => ds.state === 'materialized';

/** Discriminator: is `ds` a `LazyDataset`? Mirrors `isMaterialized`. */
export const isLazy = (ds: Dataset): ds is LazyDataset => ds.state === 'lazy';

/**
 * Internal helper for the state-narrowing pair `expectMaterialized` /
 * `expectLazy`. Both first narrow to `Dataset` (via `expectDataset`),
 * then check the `state` discriminator; the actual/expected labels
 * differ between the two callers. Centralised here so the
 * "expectDataset → predicate → TypeMismatchError" sequence lives in one
 * place — the SSOT for state-discrimination narrowing.
 */
const expectDatasetState = <T extends Dataset>(
  value: Value,
  predicate: (ds: Dataset) => ds is T,
  actual: string,
  expected: string,
  context: string,
  pos: SourcePos,
): T => {
  const ds = expectDataset(value, context, pos);
  if (!predicate(ds)) {
    throw new TypeMismatchError(actual, expected, context, pos);
  }
  return ds;
};

/**
 * Narrow a `Value` to a `MaterializedDataset`, throwing
 * `TypeMismatchError` if it's anything else (including a `LazyDataset`).
 * Use at boundaries that require materialised columns (arquero
 * conversion, verb-apply input, ...). `context` is the op-call fragment
 * shown in the error.
 */
export const expectMaterialized = (value: Value, context: string, pos: SourcePos): MaterializedDataset =>
  expectDatasetState(value, isMaterialized, 'a lazy Dataset', 'a materialized Dataset', context, pos);

/**
 * Narrow a `Value` to a `LazyDataset`, throwing `TypeMismatchError` if
 * it's anything else (including a `MaterializedDataset`). Mirrors
 * `expectMaterialized`.
 */
export const expectLazy = (value: Value, context: string, pos: SourcePos): LazyDataset =>
  expectDatasetState(value, isLazy, 'a materialized Dataset', 'a lazy Dataset', context, pos);
/** Discriminator: is `v` a `ColumnRef` Value? */
export const isColumnRef = (v: Value): v is ColumnRef => v instanceof ColumnRef;
/** Discriminator: is `v` a `DescSpec` Value? */
export const isDescSpec = (v: Value): v is DescSpec => v instanceof DescSpec;
/** Discriminator: is `v` a `DashboardSpec` Value? */
export const isDashboardSpec = (v: Value): v is DashboardSpec => v instanceof DashboardSpec;

/**
 * Narrow a `Value` to a `ColumnRef`, throwing `TypeMismatchError` if
 * the runtime kind is anything else. Mirrors `expectDataset` /
 * `expectSchema` (built from the same `expectValueKind` factory) so
 * the "I expected a ColumnRef; got a ..." failure mode lives in one
 * place.
 */
export const expectColumnRef = expectValueKind(isColumnRef, 'a ColumnRef');
/**
 * Narrow a `Value` to a `DescSpec`, throwing `TypeMismatchError` if the
 * runtime kind is anything else. Mirrors `expectColumnRef` — both flow
 * through the shared `expectValueKind` factory so all narrowers share
 * one diagnostic message shape.
 */
export const expectDescSpec = expectValueKind(isDescSpec, 'a DescSpec');
/**
 * Narrow a `Value` to a `DashboardSpec`, throwing `TypeMismatchError`
 * if the runtime kind is anything else. Mirrors `expectDescSpec` /
 * `expectColumnRef` — built from the same `expectValueKind` factory so
 * the "I expected a DashboardSpec; got a ..." failure mode lives in one
 * place. Consumed by the chat plugin's `ShowDashboardTool` execute
 * boundary (4c.8) to narrow the evaluated expression result.
 */
export const expectDashboardSpec = expectValueKind(isDashboardSpec, 'a DashboardSpec');
